import React, {
  useImperativeHandle,
  forwardRef,
  useRef,
  useState,
  useEffect
} from 'react';
import { IconButton, Box, Stack, useTheme } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Yup from 'utils/validation';
import { CandidateProfileChip } from 'modules/candidate-profile/components/CandidateProfileChip';
import { candidateProfileSearchInputSx } from 'modules/candidate-profile/styles';
import {
  suggestionKey,
  SelectedSuggestion
} from 'modules/candidate-profile/types/SelectedSuggestion';

// NOTE: Using react-hook-form directly instead of the Form builder component because
// this input requires complex custom configuration with chips and other components
// embedded within the textarea field. Direct usage provides more flexibility for
// adding additional complex UI elements as the feature evolves.

// Schema defined inline since this is a custom form component
const searchInputSchema = Yup.object().shape({
  searchQuery: Yup.string().default('')
});

type SearchInputSchema = Yup.InferType<typeof searchInputSchema>;

const PLACEHOLDER_ROTATION = [
  'Find me SEO experts in London',
  'I need a product designer with 5+ years of experience',
  'data AND (analyst OR scientist) AND NOT Perth',
  'Source some Edinburgh-based trauma nurses',
  'We’re hiring a product marketing manager with Jira experience',
  'Put together a list of C++ engineers who currently work at Google',
  'social AND media AND (manager OR director)',
  'Look for someone who knows Figma, Photoshop and InDesign',
  'Show me Ivy League graduates who work in finance',
  '(Austin OR Dallas OR Houston) AND “spatial design”'
];

const PLACEHOLDER_ROTATION_INTERVAL_MS = 3000;
const PLACEHOLDER_FADE_MS = 300;

// Once the conversation has started, the examples have done their job -- the
// box is for refining, not for learning what to type.
const REFINE_PLACEHOLDER = 'Refine your results...';

type CandidateProfileSearchInputProps = {
  isLoading: boolean;
  /** True once a message has been sent in this conversation. */
  hasConversation: boolean;
  selectedSuggestions: SelectedSuggestion[];
  onSubmit: (searchQuery: string) => void;
  onChipClick: (chip: string, field?: string) => void;
};

export type CandidateProfileSearchInputRef = {
  setValue: (value: string) => void;
  focus: () => void;
};

export const CandidateProfileSearchInput = forwardRef<
  CandidateProfileSearchInputRef,
  CandidateProfileSearchInputProps
>(
  (
    {
      isLoading,
      hasConversation,
      selectedSuggestions = [],
      onSubmit,
      onChipClick
    },
    ref
  ) => {
    const { control, handleSubmit, reset, setValue } =
      useForm<SearchInputSchema>({
        resolver: yupResolver(searchInputSchema),
        defaultValues: {
          searchQuery: ''
        },
        mode: 'onSubmit'
      });

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const [placeholderVisible, setPlaceholderVisible] = useState(true);

    useEffect(() => {
      // Nothing to rotate once the placeholder is fixed, and leaving the
      // interval running would keep fading a static string in and out.
      if (hasConversation) {
        setPlaceholderVisible(true);
        return;
      }

      const interval = setInterval(() => {
        setPlaceholderVisible(false);
        setTimeout(() => {
          setPlaceholderIndex(
            (prev) => (prev + 1) % PLACEHOLDER_ROTATION.length
          );
          setPlaceholderVisible(true);
        }, PLACEHOLDER_FADE_MS);
      }, PLACEHOLDER_ROTATION_INTERVAL_MS);
      return () => clearInterval(interval);
    }, [hasConversation]);

    // Expose setValue and focus to parent via ref
    useImperativeHandle(ref, () => ({
      setValue: (value: string) => setValue('searchQuery', value),
      focus: () => textareaRef.current?.focus()
    }));

    const handleChipClickWithFocus = (chip: SelectedSuggestion) => {
      onChipClick?.(chip.text, chip.field);
      // Focus the textarea after chip selection so Enter key can be used
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    };

    const theme = useTheme();

    const handleFormSubmit = (data: SearchInputSchema) => {
      if (isLoading) {
        return;
      }

      // Allow submission if there are chips or text
      const hasContent =
        data.searchQuery?.trim() || selectedSuggestions.length > 0;
      if (!hasContent) {
        return;
      }

      onSubmit(data.searchQuery || '');
      reset();
    };

    return (
      <Box
        component="form"
        onSubmit={handleSubmit(handleFormSubmit)}
        sx={candidateProfileSearchInputSx.form}
      >
        {/* Custom wrapper with AI gradient border */}
        <Box sx={candidateProfileSearchInputSx.wrapper(theme)}>
          {/* Selected chips - stacked above the textarea so wrapped rows push it
              down instead of covering it */}
          {selectedSuggestions.length > 0 && (
            <Stack
              direction="row"
              gap={1}
              sx={candidateProfileSearchInputSx.selectedChipsContainer}
            >
              {selectedSuggestions.map((chip) => (
                <CandidateProfileChip
                  key={suggestionKey(chip)}
                  label={chip.text}
                  size="small"
                  onClick={() => handleChipClickWithFocus(chip)}
                />
              ))}
            </Stack>
          )}

          {/* Textarea fills whatever height the chips leave */}
          <Controller
            name="searchQuery"
            control={control}
            render={({ field }) => (
              <Box
                component="textarea"
                {...field}
                ref={(e: HTMLTextAreaElement | null) => {
                  field.ref(e);
                  if (e) {
                    (
                      textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>
                    ).current = e;
                  }
                }}
                placeholder={
                  hasConversation
                    ? REFINE_PLACEHOLDER
                    : PLACEHOLDER_ROTATION[placeholderIndex]
                }
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(handleFormSubmit)();
                  }
                }}
                sx={candidateProfileSearchInputSx.searchInput(
                  placeholderVisible ? 1 : 0
                )}
              />
            )}
          />
        </Box>

        {/* Send button - bottom right */}
        <Box sx={candidateProfileSearchInputSx.sendButtonContainer}>
          <IconButton
            type="submit"
            sx={candidateProfileSearchInputSx.sendButton}
            color="primary"
            disabled={isLoading}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    );
  }
);
